/**
 * Inline patient picker for the Projects agent.
 *
 * Used when the agent needs the clinician to choose which patient to act
 * on (e.g. "Which patient should I use to fill the form?"): it asks
 * ``ask_user`` with ``kind: 'pick_patient'`` and no options, and this list
 * renders recent patients (self-fetched, searchable) instead of a blind
 * free-text box. Supports single OR multi select.
 *
 * Selection is owned by the parent (the clarification's ``selectedIds`` +
 * ``toggleOption``) so it plugs straight into the existing single/multi
 * answer machinery; this component just renders rows and reports toggles.
 *
 * Prefer the real ``patientId`` when the session is linked to a profile;
 * fall back to the patient name so unlinked patients remain selectable
 * (the agent can still resolve by name).
 *
 * The rows come from the app's review-context sources, so they cross as the
 * ``useLinkableItems`` hook port; the search query stays here, as picker state.
 */

import { SearchInput } from '@heidi/design-system/search-input';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Check, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { patientSelectionId } from '../../../data/projects/components/patientSelectionId';
import type { LinkableContextItems } from '../../../data/projects/useLinkableContextItems';
import { useLinkableContextItems } from '../hooks/useLinkableContextItems';

export interface PickedPatient {
  id: string;
  name: string;
}

export interface PatientPickerListProps {
  /** Ids currently selected (drives the row highlight / check). */
  selectedIds: ReadonlySet<string>;
  /** Toggle a patient in/out of the selection (parent enforces the cap). */
  onToggle: (patient: PickedPatient) => void;
  disabled?: boolean;
  className?: string;
}

/** What the app supplies: the recent-patient rows for a search query. */
export interface PatientPickerListHost {
  /** Called unconditionally with the picker's query. Module-level, so its identity is stable. */
  useLinkableItems: (args: {
    query: string;
    limit: number;
  }) => Pick<LinkableContextItems, 'patientItems'>;
}

// A glance surface, roomier than the @-mention default so the user can
// find a patient without always searching.
const PATIENT_LIMIT = 25;

export const PatientPickerListView = ({
  selectedIds,
  onToggle,
  disabled = false,
  className,
  useLinkableItems,
}: PatientPickerListProps & PatientPickerListHost): JSX.Element => {
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const { patientItems } = useLinkableItems({ query, limit: PATIENT_LIMIT });

  return (
    <div className={cn('flex flex-col gap-2', className)} data-testid="patient-picker">
      <SearchInput
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
        placeholder={intl.formatMessage({
          id: 'q903qirvhJ',
          defaultMessage: 'Search patients by name',
        })}
        aria-label="Search patients"
        clearAriaLabel={intl.formatMessage({
          defaultMessage: 'Clear search',
          id: '4YJHutR26p',
        })}
      />

      <div className="flex max-h-64 flex-col gap-1 overflow-y-auto [scrollbar-gutter:stable]">
        {patientItems.length === 0 ? (
          <TypographyCaption className="px-3 py-4 text-text-secondary">
            {query.trim()
              ? intl.formatMessage({
                  id: 'IpVxYRjzuA',
                  defaultMessage: 'No patients match your search.',
                })
              : intl.formatMessage({
                  id: 'SamQwxWqpC',
                  defaultMessage: 'No recent patients to choose from.',
                })}
          </TypographyCaption>
        ) : (
          patientItems.map((item) => {
            if (item.type !== 'patient') return null;
            const id = patientSelectionId(item);
            const isSelected = selectedIds.has(id);
            return (
              <button
                key={id}
                type="button"
                disabled={disabled}
                onClick={() => onToggle({ id, name: item.patientName })}
                aria-pressed={isSelected}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-start transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected
                    ? 'border-border-pressed bg-background-secondary'
                    : 'border-border bg-surface hover:bg-surface-hover'
                )}
                data-testid="patient-picker-row"
              >
                <UserRound className="size-4 shrink-0 text-text-secondary" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                  {item.patientName}
                </span>
                {isSelected ? (
                  <Check className="size-4 shrink-0 text-text-active" aria-hidden />
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

/** Binds the patient picker to the linkable-context rows. */
export const PatientPickerList = (props: PatientPickerListProps): JSX.Element => (
  <PatientPickerListView {...props} useLinkableItems={useLinkableContextItems} />
);
